// Amplify configuration for AWS Cognito
// These values will be populated from CDK outputs after deployment
//
// NOTE: We use the "Tenant Users" app client for authenticated tenant users
// (admins and regular users). The "Respondents" app client is for future use
// with optional respondent authentication.
//
// We only use User Pool authentication (no Identity Pool).
// Identity Pool is for granting AWS credentials to unauthenticated users,
// which we don't need for this application.

// Build OAuth configuration if Cognito domain is available
// VITE_COGNITO_DOMAIN can be either:
// - A custom domain (e.g., "auth.coformit.dev") - used directly
// - A legacy prefix (e.g., "coformit-coformit-dev") - constructed with region
const cognitoDomainValue = import.meta.env.VITE_COGNITO_DOMAIN
const awsRegion = import.meta.env.VITE_AWS_REGION || 'us-east-2'
// Amplify v6 OAuth domain needs the full Cognito hosted UI domain (without https://)
// If the value contains a dot, it's a custom domain; otherwise, it's a prefix to construct
const cognitoDomain = cognitoDomainValue
  ? (cognitoDomainValue.includes('.') ? cognitoDomainValue : `${cognitoDomainValue}.auth.${awsRegion}.amazoncognito.com`)
  : null
const appUrl = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3000'

// PKCE helper functions for OAuth 2.0 with login_hint support
const SSO_PKCE_KEY = 'sso_pkce_verifier'

/**
 * Generate a cryptographically random string for PKCE code_verifier
 */
function generateCodeVerifier() {
  const array = new Uint8Array(32)
  crypto.getRandomValues(array)
  return btoa(String.fromCharCode(...array))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Generate SHA-256 hash and base64url encode for PKCE code_challenge
 */
async function generateCodeChallenge(verifier) {
  const encoder = new TextEncoder()
  const data = encoder.encode(verifier)
  const digest = await crypto.subtle.digest('SHA-256', data)
  return btoa(String.fromCharCode(...new Uint8Array(digest)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '')
}

/**
 * Store PKCE code_verifier for later use during token exchange
 */
export function storePkceVerifier(verifier) {
  sessionStorage.setItem(SSO_PKCE_KEY, verifier)
}

/**
 * Retrieve and clear the stored PKCE code_verifier
 */
export function retrievePkceVerifier() {
  const verifier = sessionStorage.getItem(SSO_PKCE_KEY)
  sessionStorage.removeItem(SSO_PKCE_KEY)
  return verifier
}

export const amplifyConfig = {
  Auth: {
    Cognito: {
      userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID || 'placeholder-user-pool-id',
      userPoolClientId: import.meta.env.VITE_COGNITO_CLIENT_ID || 'placeholder-client-id',
      // NO identityPoolId needed - we're only using User Pool authentication
      signUpVerificationMethod: 'code',
      loginWith: {
        email: true,
        username: false,
        // OAuth configuration for SSO (if Cognito domain is configured)
        ...(cognitoDomain && {
          oauth: {
            domain: cognitoDomain,
            scopes: ['openid', 'email', 'profile'],
            redirectSignIn: [`${appUrl}/auth/callback`],
            redirectSignOut: [`${appUrl}/logout`],
            responseType: 'code',
          }
        })
      }
    }
  }
}

/**
 * Build SSO authorization URL with PKCE and login_hint support.
 * This allows pre-filling the user's email at the identity provider
 * while maintaining security through PKCE.
 *
 * @param {string} providerName - The Cognito identity provider name
 * @param {string} email - The user's email to pre-fill (optional)
 * @returns {Promise<string|null>} The authorization URL, or null if OAuth is not configured
 */
export async function buildSsoAuthUrl(providerName, email = null) {
  if (!cognitoDomain) {
    console.warn('Cognito domain not configured, cannot build SSO URL')
    return null
  }

  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
  if (!clientId) {
    console.warn('Cognito client ID not configured')
    return null
  }

  // Generate PKCE parameters
  const codeVerifier = generateCodeVerifier()
  const codeChallenge = await generateCodeChallenge(codeVerifier)

  // Store verifier for token exchange on callback
  storePkceVerifier(codeVerifier)

  const redirectUri = `${appUrl}/auth/callback`
  const scopes = 'openid email profile'

  // Build the authorization URL with PKCE
  const params = new URLSearchParams({
    client_id: clientId,
    response_type: 'code',
    scope: scopes,
    redirect_uri: redirectUri,
    identity_provider: providerName,
    code_challenge: codeChallenge,
    code_challenge_method: 'S256',
  })

  // Add login_hint if email is provided (pre-fills email at IdP)
  if (email) {
    params.set('login_hint', email.toLowerCase().trim())
  }

  return `https://${cognitoDomain}/oauth2/authorize?${params.toString()}`
}

/**
 * Exchange authorization code for tokens using Cognito's token endpoint.
 * This is used when we do manual OAuth redirect (for login_hint support)
 * instead of Amplify's signInWithRedirect.
 *
 * @param {string} code - The authorization code from the callback URL
 * @returns {Promise<{idToken: string, accessToken: string, refreshToken: string}>}
 */
export async function exchangeCodeForTokens(code) {
  if (!cognitoDomain) {
    throw new Error('Cognito domain not configured')
  }

  const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID
  if (!clientId) {
    throw new Error('Cognito client ID not configured')
  }

  const codeVerifier = retrievePkceVerifier()
  if (!codeVerifier) {
    throw new Error('PKCE code verifier not found - OAuth flow may have been interrupted')
  }

  const redirectUri = `${appUrl}/auth/callback`
  const tokenEndpoint = `https://${cognitoDomain}/oauth2/token`

  const params = new URLSearchParams({
    grant_type: 'authorization_code',
    client_id: clientId,
    code: code,
    redirect_uri: redirectUri,
    code_verifier: codeVerifier,
  })

  const response = await fetch(tokenEndpoint, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    body: params.toString(),
  })

  if (!response.ok) {
    const errorText = await response.text()
    console.error('Token exchange failed:', errorText)
    throw new Error('Failed to exchange authorization code for tokens')
  }

  const tokens = await response.json()
  return {
    idToken: tokens.id_token,
    accessToken: tokens.access_token,
    refreshToken: tokens.refresh_token,
  }
}

/**
 * Get OAuth configuration values for external use
 */
export function getOAuthConfig() {
  return {
    domain: cognitoDomain,
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirectUri: `${appUrl}/auth/callback`,
    region: awsRegion,
    userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  }
}

export default amplifyConfig
